import ModelFactoryModel, {IModelFactoryModels} from "../ModelFactoryModel";
import DataChoice, {IDataChoices} from "./DataChoice";
import DataFactory from "./DataFactory";
import {getManager} from "../../../manager/Manager";
import ApiError, {determineApiError} from "../../../manager/api/ApiError";
import {NavigationLevelVO} from "../../../manager/navigation/NavigationModel";
import {IAnyObject, IStringUndefined} from "../../../common/SimpleInterface";
import ApiRequestVO from "../../../manager/api/ApiRequestVO";
import ApiResponseVO from "../../../manager/api/ApiResonseVO";
import DeferredPromise, {createDeferredPromise} from "../../../common/DeferredPromise";
import {isNativeObject, slugToID} from "../../../common/SiteHelper";
import {dispatchDataModelUpdate} from "../../../event/DispatchEvent";

export default class DataModel extends ModelFactoryModel
{

    choices: IDataChoices = {};
    publicChoiceIDs:IStringUndefined = {};
    populated:boolean = false;
    didFailToPopulate:boolean = false;
    populating:boolean = false;
    repopulateOnAppear:boolean = false;
    onDataReceivedPromises:DeferredPromise<ApiError|null, void>[] = [];
    failedAdditionalRequestedIDs:string[] = [];

    choiceList(choiceOrder?:string[]): DataChoice[]
    {
        return super.choiceList(choiceOrder) as DataChoice[];
    }

    createChoice(object:IAnyObject): DataChoice|null
    {
        const choice = this.factory().birthDataChoice(object);
        if(!choice.id){
            return null;
        }
        return choice;
    }

    applyChoice(choice: DataChoice)
    {
        super.applyChoice(choice);
        this.publicChoiceIDs[slugToID(choice.slug)] = choice.id;
    }

    getPopulateRequestVO(requestedIDs?:string[]): ApiRequestVO|null
    {
        return null;
    }

    isPopulated():boolean
    {
        return this.populated;
    }


    onWillAppear(levelList: NavigationLevelVO[])
    {
        super.onWillAppear(levelList);
        if(this.repopulateOnAppear){
            this.resetPopulated();
        }
    }

    async onRequestResponseReceived(responseVO:ApiResponseVO, replaceData:boolean) :Promise<void> {

        const response = responseVO.data;
        let noInternetConnection = false;
        if(responseVO.apiError)
        {
            noInternetConnection = responseVO.apiError.code === "no_internet_connection";
            if(!noInternetConnection || replaceData){
                return Promise.reject(responseVO.apiError);
            }
            await getManager().storageData.populateFromStorage(true);
            if(!this.isPopulated()){
                return Promise.reject(responseVO.apiError);
            }
            return;
        }
        const hasError = !!response.has_error || !isNativeObject(response);
        let apiError = determineApiError(response.error, hasError)
        if(!apiError){

            apiError = this.handleRequestResponse(response, replaceData);
        }

        if(apiError){
            this.handleRequestError(apiError);
            return Promise.reject(apiError);
        }
        if(replaceData){
            this.cacheResponse(response);
        }
    }

    populateChoiceList(objectArray:IAnyObject[], reset:boolean):DataChoice[]
    {
        const choiceList:DataChoice[] = [];
        for(let i = 0; i < objectArray.length; i++){
            const object = objectArray[i];
            const choice = this.createChoice(object);
            if(choice){
                choiceList.push(choice);
            }
        }
        this.addToChoiceList(choiceList, reset);
        return choiceList;
    }

    addToChoiceList(fromChoiceList:DataChoice[], reset:boolean = false)
    {
        if(reset){
            this.clearChoiceList();
        } else {
            this.clearActiveChoiceList();
        }
        for(let i = 0; i < fromChoiceList.length; i++){
            const choice = fromChoiceList[i];
            this.addChoice(choice);
        }
        this.requireChoiceOrder();
        this.dispatchDataReceivedListeners();
    }

    dispatchPromises(error:ApiError|null = null) {
        for(let i = 0; i < this.onDataReceivedPromises.length; i++){
            const promise = this.onDataReceivedPromises[i];
            promise.resolve(error);
        }
        this.onDataReceivedPromises.length = 0;
    }

    async handlePopulated(error:ApiError|null = null) : Promise<boolean>
    {
        this.populating = false;
        this.populated = true;
        if(!error){
            this.didFailToPopulate = false;
            return this.onPopulated().then(() => {
                this.dispatchDataReceivedListeners();
                return true
            });
        } else {
            this.didFailToPopulate = true;
        }
        this.dispatchDataReceivedListeners(error);
        return true
    }

    async onPopulated() : Promise<void>
    {

    }

    dispatchDataReceivedListeners(error:ApiError|null = null) {
        this.dispatchPromises(error);
        this.onDataModelChanged();
    }

    handleRequestResponse(response:IAnyObject, replaceData:boolean): ApiError|null {
        if(!Object.keys(response).length){
            return new ApiError(404, "failed_to_load", "Failed to load response data")
        }
        const populateError = this.populateWithRequestResponse(response, replaceData);
        if(populateError){
            return populateError;
        }
        this.onRequestPopulated(response);
        this.populateRelatedModels(response);
        return null;
    }

    populateWithRequestResponse(response:IAnyObject, replaceData:boolean): ApiError|null {
        return null
    }

    insertRequestResponse(response: IAnyObject, replaceData:boolean): ApiError | null
    {
        const populateResponse = this.handleRequestResponse(response, replaceData);
        if(!(populateResponse instanceof ApiError))
        {
            if(replaceData){
                this.handlePopulated().then()
            } else {
                this.dispatchDataReceivedListeners();
            }
        }
        return populateResponse;
    }

    onRequestPopulated(_: IAnyObject): void
    {

    }

    populateRelatedModels(_: IAnyObject)
    {

    }

    cacheResponse(response:IAnyObject) {
        getManager().storageData.addToStorage(this.id, this.factoryID, this.populateIDs, response);
    }

    handleRequestError(apiError:ApiError) {

    }


    async requirePopulated(): Promise<ApiError|null>
    {
        if(this.isPopulated() && !this.populating){
            return null;
        }

        const promise:DeferredPromise<ApiError|null, void> = createDeferredPromise<ApiError|null, void>();

        this.onDataReceivedPromises.push(promise)

        if(this.populating){
            return promise
        }

        const requestVO: ApiRequestVO|null = this.getPopulateRequestVO();
        if(!requestVO){
            return this.handlePopulated().then(()=> {
                return promise
            });
        }
        requestVO.dataModel = this;

        return getManager().api.requestPopulateData(requestVO).then(() => {
            return promise
        });
    }

    cloneChoice(choice:DataChoice):DataChoice
    {
        return this.factory().birthDataChoice(JSON.parse(JSON.stringify(choice.receivedObject)))
    }

    async requestAdditionalChoices(fromChoiceIDs:string[], doClone:boolean = false):Promise<DataChoice[]>
    {
        return this.requirePopulated().then(() => {
            if(this.populating){
                return this.requestAdditionalChoices(fromChoiceIDs, doClone);
            }
            const choiceIDs = JSON.parse(JSON.stringify(fromChoiceIDs));
            const choiceList:DataChoice[] = [];
            for(let i = 0; i < choiceIDs.length; i++){
                const choiceID = choiceIDs[i];
                const choice = this.choices[choiceID] ?? null;
                if(choice || this.failedAdditionalRequestedIDs.includes(choiceID) || (choiceIDs.indexOf(choiceID) < i)){
                    if(choice){
                        choiceList.push(!doClone ? choice : this.cloneChoice(choice));
                    }
                    choiceIDs.splice(i, 1);
                    i --;
                }
            }
            if(!choiceIDs.length){
                return choiceList;
            }

            const requestVO: ApiRequestVO|null = this.getPopulateRequestVO(choiceIDs);
            if(!requestVO){
                return choiceList
            }
            requestVO.dataModel = this;
            requestVO.isAdditionalData = true;

            return getManager().api.requestPopulateData(requestVO).then(() => {
                for(let choiceID of fromChoiceIDs)
                {
                    const choice = this.choices[choiceID] ?? null;
                    if(choice){
                        choiceList.push(!doClone ? choice : this.cloneChoice(choice));
                    } else if(choiceIDs.includes(choiceID) && !this.failedAdditionalRequestedIDs.includes(choiceID)){
                        this.failedAdditionalRequestedIDs.push(choiceID);
                    }
                }
                return choiceList;
            }).catch(() => {
                return choiceList;
            });
        })
    }

    factory(): DataFactory
    {
        return super.factory() as DataFactory;
    }

    onDataModelChanged():void
    {
        dispatchDataModelUpdate(this);
    }

    resetData(): void
    {
        this.resetChoiceList();
        this.resetPopulated();
    }

    resetPopulated(): void
    {
        this.populated = false;
        this.populating = false;
        this.didFailToPopulate = false;
    }

    destroy()
    {
        this.dispatchPromises();
        super.destroy();
    }


    afterDestroyed()
    {
        super.afterDestroyed();
        this.onDataModelChanged();
    }
}

export interface IDataModels extends IModelFactoryModels
{
    [key: string]: DataModel;
}